/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { Tooltip, type TablePaginationConfig, type TableProps } from 'antd';
import { ICollectorReportRes } from '@/interfaces/report';
import { FileSearchOutlined } from '@ant-design/icons';
import {
  FilterValue,
  SorterResult,
  TableRowSelection,
} from 'antd/es/table/interface';
import { EditFilled, InfoCircleOutlined } from '@ant-design/icons';
import TableComponent from '@/components/table';
import { formatDate, formatNumber } from '@/utils/dataUtils';
import dayjs from 'dayjs';
import { twMerge } from 'tailwind-merge';
import {
  getPaymentReceivedStatusName,
  getPaymentReceivedStatusColor,
  getPaymentTypeColor,
  getPaymentTypeName,
} from '@/helpers/orderHelper';
import { IMedia } from '@/interfaces/media';
import { mediaView } from '@/utils/mediaUtils';

interface IProps {
  margin: number;
  loading: boolean;
  report: ICollectorReportRes[];
  onTableChange: (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<ICollectorReportRes>
      | SorterResult<ICollectorReportRes>[],
  ) => void;
  selectedRowKeys: React.Key[];
  onSelectChange: (
    newSelectedRowKeys: React.Key[],
    rows: ICollectorReportRes[],
  ) => void;
  isSelect: boolean;
  onEdit: (record: ICollectorReportRes) => void;
}

const SafetyStockReportTable = ({
  loading,
  report,
  onTableChange,
  selectedRowKeys,
  onSelectChange,
  isSelect,
  onEdit,
}: IProps) => {
  const rowSelection: TableRowSelection<ICollectorReportRes> = {
    selectedRowKeys,
    onChange: onSelectChange,
    // getCheckboxProps: (record: ICollectorReportRes) => ({
    //   disabled:
    //     !!record.receivedId ||
    //     !!(
    //       selectedRowKeys.length &&
    //       !selectedRowKeys.includes(record.id as number)
    //     ),
    // }),
    columnTitle: <span style={{ opacity: 0.5, cursor: 'not-allowed' }}></span>,
    onSelectAll: () => {
      return false;
    },
  };

  const handleDownload = async (mediaId: number, name: string) => {
    const media: IMedia = {
      id: mediaId,
      name: name,
    } as IMedia;
    await mediaView(media);
  };

  const columns: TableProps<ICollectorReportRes>['columns'] = [
    {
      title: '#',
      dataIndex: 'id',
      key: 'id',
      width: 50,
      align: 'center',
      fixed: 'left',
      className: 'text-center',
      render: (_, rec, i) => <div>{i + 1}</div>,
    },
    {
      title: 'Inv No.',
      dataIndex: 'ivNo',
      key: 'ivNo',
      className: 'font-bold',
      align: 'center',
      width: 120,
      fixed: 'left',
    },
    {
      title: 'AWB',
      dataIndex: 'billingNo',
      key: 'billingNo',
      className: 'font-bold',
      align: 'center',
      width: 120,
      render: (_, rec) => <div>{!rec.billingDate ? _ : rec.jobCode}</div>,
    },
    {
      title: 'Customer Code',
      dataIndex: 'customerCode',
      key: 'customerCode',
      className: 'font-bold',
      width: 120,
      align: 'center',
      sorter: (a, b) =>
        (a.customerCode || '').localeCompare(b.customerCode || ''),
    },
    {
      title: 'Customer name',
      dataIndex: 'customerName',
      key: 'customerName',
      className: 'font-bold',
      sorter: (a, b) =>
        (a.customerName || '').localeCompare(b.customerName || ''),
      render: (_, rec) => (
        <div className="flex gap-1 items-center">
          <div className="truncate">{_}</div>
          {rec.remark && (
            <Tooltip placement="top" title={rec.remark}>
              <InfoCircleOutlined className="text-red-700" />
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: 'BU',
      dataIndex: 'businessUnitName',
      key: 'businessUnitName',
      className: 'font-bold',
      align: 'center',
      width: 80,
    },

    {
      title: 'Sales (THB)',
      dataIndex: 'orderAmount',
      key: 'orderAmount',
      className: 'font-bold text-blue-700',
      align: 'right',
      sorter: (a, b) => Number(a.orderAmount) - Number(b.orderAmount),
      render: (_: number) => formatNumber(_, 2),
      width: 120,
    },
    {
      title: 'ประเภท',
      dataIndex: 'paymentType',
      key: 'paymentType',
      align: 'center',
      width: 100,
      render: (_, rec) => {
        const name = getPaymentTypeName(_);
        const status = getPaymentTypeColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-1 px-1',
            )}
          >
            <Tooltip placement="top" title={rec.chequeNo}>
              {name}
            </Tooltip>
          </div>
        );
      },
    },
    {
      title: 'วันที่สั่งซื้อ',
      dataIndex: 'orderDate',
      key: 'orderDate',
      align: 'center',
      width: 100,
      sorter: (a, b) => dayjs(a.orderDate).unix() - dayjs(b.orderDate).unix(),
      render: _ => formatDate(_),
    },
    {
      title: 'วันที่วางบิล',
      dataIndex: 'billingDate',
      key: 'billingDate',
      align: 'center',
      width: 100,
      sorter: (a, b) =>
        dayjs(a.billingDate).unix() - dayjs(b.billingDate).unix(),
      render: (_, rec) => {
        const diff = dayjs().diff(dayjs(rec.orderDate), 'day');
        const isOverDeal = diff > 30;
        return (
          <div
            className={twMerge(
              isOverDeal && !rec.billingDate && 'text-red-500',
            )}
          >
            {_ ? formatDate(_) : isOverDeal && `เกินมา ${diff} วัน`}
          </div>
        );
      },
    },
    {
      title: 'Day',
      dataIndex: 'billingDate',
      key: 'billingDate',
      align: 'center',
      sorter: (a, b) =>
        dayjs(a.billingDate).diff(dayjs(a.orderDate), 'day') -
        dayjs(b.billingDate).diff(dayjs(b.orderDate), 'day'),
      width: 50,
      render: (_, rec) => {
        const diff =
          (rec.billingDate &&
            dayjs(rec.billingDate).diff(dayjs(rec.orderDate), 'day')) ||
          0;
        return <div>{!rec.billingDate ? '' : diff}</div>;
      },
    },
    {
      title: 'วันที่ครบกำหนด',
      dataIndex: 'dueDate',
      key: 'dueDate',
      align: 'center',
      sorter: (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      defaultSortOrder: 'ascend',
      width: 100,
      render: (_, rec) => {
        const diff = dayjs().diff(dayjs(_), 'day');
        const isOverDeal = diff > -1;
        return (
          <div
            className={twMerge(isOverDeal && !rec.paidDate && 'text-red-500')}
          >
            {_ ? formatDate(_) : isOverDeal && `เกินมา ${diff} วัน`}
          </div>
        );
      },
    },
    {
      title: 'วันที่รับเงิน',
      dataIndex: 'paidDate',
      key: 'paidDate',
      align: 'center',
      sorter: (a, b) => dayjs(a.paidDate).unix() - dayjs(b.paidDate).unix(),
      width: 100,
      render: (_, rec) => {
        const diff = dayjs().diff(dayjs(rec.dueDate), 'day');
        const isOverDeal = diff > 90;
        return (
          <div
            className={twMerge(
              isOverDeal && !rec.paidDate && 'text-red-500 font-bold',
            )}
          >
            {_
              ? formatDate(dayjs(_).add(3, 'day'))
              : isOverDeal && `เกินมา ${diff} วัน`}
          </div>
        );
      },
    },
    {
      title: 'Day',
      dataIndex: 'paidDay',
      key: 'paidDay',
      align: 'center',
      sorter: (a, b) => dayjs(a.orderDate).unix() - dayjs(b.orderDate).unix(),
      width: 50,
      render: (a, rec) => {
        const diff =
          (rec.paidDate &&
            dayjs(rec.paidDate).diff(dayjs(rec.dueDate), 'day')) ||
          0;
        const diffDueDate = dayjs().diff(dayjs(rec.dueDate), 'day');
        return (
          <div
            className={twMerge(
              !rec.paidDate && 'text-gray-200',
              !rec.paidDate && diffDueDate > 90 && 'text-red-600 font-bold',
            )}
          >
            {!rec.paidDate ? diffDueDate : diff}
          </div>
        );
      },
    },
    {
      title: 'ยอดรับ',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      className: '',
      align: 'right',
      sorter: (a, b) => Number(a.paidAmount) - Number(b.paidAmount),
      render: (_: number) => formatNumber(_ || 0, 2),
      width: 110,
    },
    {
      title: 'คงค้าง',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      className: '',
      align: 'right',
      render: (_: number, rec) =>
        _ && rec.orderAmount - _
          ? formatNumber(Number(rec.orderAmount - _) || 0, 2)
          : '',
      width: 110,
    },
    {
      title: 'FILE',
      dataIndex: 'receivedId',
      key: 'receivedId',
      width: 50,
      align: 'center',
      render: (_, rec) =>
        _ && (
          <FileSearchOutlined
            className="text-blue-700 px-1.5"
            onClick={() => handleDownload(_, rec.chequeNo as string)}
          />
        ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 130,
      fixed: 'right',
      render: _ => {
        const name = getPaymentReceivedStatusName(_);
        const status = getPaymentReceivedStatusColor(_);
        return (
          <div
            className={twMerge(
              'truncate',
              status?.bg,
              status?.text,
              'rounded py-1 px-1',
            )}
          >
            {name}
          </div>
        );
      },
    },
    {
      dataIndex: 'action',
      key: 'action',
      width: 40,
      fixed: 'right',
      render: (_, rec) => (
        <div className="flex flex-row gap-4 items-center w-full justify-center">
          <div className="flex flex-row divide-x divide-dashed items-center w-full justify-center">
            <EditFilled
              className="text-red-500"
              onClick={() => onEdit && onEdit(rec)}
            />
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <TableComponent<ICollectorReportRes>
        columns={columns}
        criteria={undefined}
        data={{ data: report, totalRecords: report.length || 0 }}
        loading={loading}
        onTableChange={onTableChange}
        showPagination={false}
        rowSelection={isSelect ? rowSelection : undefined}
        scrollLength={1800}
      />
    </div>
  );
};

export default SafetyStockReportTable;
